// frontend/src/lib/platformSecurityCapabilityService.js
// Platform Security capability service (ADR-SEC-001 / CES-COP-001B)
//
// Thin, stateless wrapper around the platform_security_capability_check
// Edge Function. This module never queries a Platform Security table or
// RPC directly, never reads an email allowlist, and never reads a
// REACT_APP_* authority value: the browser's only path to Platform Security
// state is this Edge Function call. This module holds no React state and
// no persisted cache — callers (e.g. PlatformSecurityCapabilityContext) own
// all caching and invalidation.
//
// © 2026 Advanced Advisory Services, LLC. All Rights Reserved.

import { supabase } from "./supabaseClient";

const FUNCTION_NAME = "platform_security_capability_check";

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function normalizeCapabilityCodes(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const out = [];
  value.forEach((raw) => {
    if (!isNonEmptyString(raw)) return;
    const code = raw.trim();
    if (seen.has(code)) return;
    seen.add(code);
    out.push(code);
  });
  out.sort();
  return out;
}

function failureResult(message) {
  return {
    ok: false,
    userId: null,
    capabilities: [],
    generatedAt: null,
    error: message,
  };
}

function safeMessage(value, fallback) {
  if (isNonEmptyString(value)) return value.trim();
  if (value && isNonEmptyString(value.message)) return value.message.trim();
  return fallback;
}

/**
 * Fetches the authenticated caller's own effective Platform Security
 * capability codes via the platform_security_capability_check Edge
 * Function. Never throws: every outcome, including a missing session, a
 * network failure, or a malformed response, is normalized into a
 * predictable result object.
 *
 * Success:
 *   { ok: true, userId, capabilities: string[], generatedAt, error: null }
 * Failure:
 *   { ok: false, userId: null, capabilities: [], generatedAt: null, error }
 */
export async function fetchEffectivePlatformSecurityCapabilities() {
  try {
    const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
      body: {},
    });

    if (error) {
      return failureResult(
        safeMessage(error, "platform_security_capability_check_failed")
      );
    }

    if (!data || typeof data !== "object" || Array.isArray(data)) {
      return failureResult("invalid_platform_security_capability_response");
    }

    if (data.ok !== true) {
      return failureResult(
        safeMessage(data.error, "platform_security_capability_check_failed")
      );
    }

    if (!isNonEmptyString(data.user_id)) {
      return failureResult("invalid_platform_security_capability_response");
    }

    return {
      ok: true,
      userId: data.user_id.trim(),
      capabilities: normalizeCapabilityCodes(data.capabilities),
      generatedAt: isNonEmptyString(data.generated_at)
        ? data.generated_at.trim()
        : null,
      error: null,
    };
  } catch (err) {
    return failureResult(
      safeMessage(err, "platform_security_capability_check_failed")
    );
  }
}
